import React, { useState, useRef } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Upload, FileCheck, AlertCircle, CheckCircle2, XCircle, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const DOC_TYPES = [
  { id: 'pan', label: 'PAN Card', help: 'Of company / proprietor / signatory' },
  { id: 'aadhaar', label: 'Aadhaar Card', help: 'Of authorised signatory' },
  { id: 'signatory', label: 'Signatory KYC', help: 'Photo ID + address proof' },
];

const KYC_BADGES = {
  pending: { bg: 'bg-slate-500/10', text: 'text-slate-600', label: 'KYC Pending', icon: AlertCircle },
  in_progress: { bg: 'bg-blue-500/10', text: 'text-blue-600', label: 'In Progress', icon: Upload },
  submitted: { bg: 'bg-amber-500/10', text: 'text-amber-600', label: 'Under Review', icon: FileCheck },
  approved: { bg: 'bg-green-500/10', text: 'text-green-600', label: 'KYC Approved', icon: CheckCircle2 },
  rejected: { bg: 'bg-red-500/10', text: 'text-red-600', label: 'KYC Rejected — Re-upload Needed', icon: XCircle },
};

const ClientKYCCard = ({ sub, api, onUpdated }) => {
  const [uploading, setUploading] = useState(null);
  const inputs = useRef({});
  const status = sub.kyc_status || 'pending';
  const docs = sub.kyc_documents || {};
  const badge = KYC_BADGES[status] || KYC_BADGES.pending;
  const BadgeIcon = badge.icon;

  if (sub.status !== 'active') return null;

  const handleUpload = async (docType, file) => {
    if (!file) return;
    setUploading(docType);
    try {
      const form = new FormData();
      form.append('doc_type', docType);
      form.append('file', file);
      await api.post(`/virtual-office/subscriptions/${sub.id}/kyc/upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`${DOC_TYPES.find((d) => d.id === docType).label} uploaded`);
      onUpdated && onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(null);
      if (inputs.current[docType]) inputs.current[docType].value = '';
    }
  };

  return (
    <Card className="mt-4 border-dashed" data-testid={`kyc-card-${sub.id}`}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h4 className="font-semibold flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-primary" /> KYC Documents
          </h4>
          <span className={`text-xs px-2 py-0.5 rounded-full ${badge.bg} ${badge.text} flex items-center gap-1 font-medium`}>
            <BadgeIcon className="w-3 h-3" /> {badge.label}
          </span>
        </div>

        {status === 'approved' && (
          <div className="text-sm text-green-700 dark:text-green-400 mb-3">
            ✓ All documents verified. Your GST / company registration can now proceed.
          </div>
        )}
        {status === 'rejected' && sub.kyc_review_comment && (
          <div className="text-sm text-red-700 dark:text-red-400 mb-3 p-2.5 rounded bg-red-50 dark:bg-red-500/10">
            <strong>Reviewer comment:</strong> {sub.kyc_review_comment}
          </div>
        )}
        {status === 'submitted' && (
          <p className="text-sm text-muted-foreground mb-3">All 3 documents uploaded. Our team will review within 1-2 business days.</p>
        )}

        <div className="space-y-2">
          {DOC_TYPES.map((d) => {
            const uploaded = docs[d.id];
            return (
              <div key={d.id} className="flex flex-wrap items-center gap-3 p-2.5 rounded border" data-testid={`kyc-row-${d.id}`}>
                <div className="flex-1 min-w-[160px]">
                  <p className="font-medium text-sm">{d.label} {uploaded && <CheckCircle2 className="w-3 h-3 inline text-green-500 ml-1" />}</p>
                  <p className="text-xs text-muted-foreground">{d.help}</p>
                  {uploaded && <p className="text-[10px] text-muted-foreground mt-0.5 truncate">{uploaded.filename}</p>}
                </div>
                <input
                  type="file"
                  accept="application/pdf,image/jpeg,image/png,image/webp"
                  ref={(el) => { inputs.current[d.id] = el; }}
                  onChange={(e) => handleUpload(d.id, e.target.files?.[0])}
                  className="hidden"
                  data-testid={`kyc-input-${d.id}`}
                />
                <Button
                  size="sm"
                  variant={uploaded ? 'outline' : 'default'}
                  onClick={() => inputs.current[d.id]?.click()}
                  disabled={uploading === d.id || status === 'approved'}
                  data-testid={`kyc-upload-${d.id}`}
                >
                  {uploading === d.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
                  {uploaded ? 'Re-upload' : 'Upload'}
                </Button>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground mt-3">Accepted: PDF, JPG, PNG, WEBP · Max 10 MB each</p>
      </CardContent>
    </Card>
  );
};

export default ClientKYCCard;
